import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, Subscription, interval, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { MaintenanceService } from '@riise/core/services/maintenance/maintenance.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'env';

@Injectable({
  providedIn: 'root',
})
export class NetworkService implements OnDestroy {
  private maintenanceService = inject(MaintenanceService);
  private http = inject(HttpClient);

  private online = new BehaviorSubject<boolean>(navigator.onLine);
  private showSnackbar = new BehaviorSubject<boolean>(false);
  private message = new BehaviorSubject<string>('');
  private snackbarClass = new BehaviorSubject<string>('');
  private previousConnectionState = new BehaviorSubject<string>(navigator.onLine ? 'online' : 'offline');

  online$ = this.online.asObservable();
  showSnackbar$ = this.showSnackbar.asObservable();
  message$ = this.message.asObservable();
  snackbarClass$ = this.snackbarClass.asObservable();

  private subscription: Subscription = new Subscription();
  private pollingSubscription: Subscription | null = null;

  constructor() {
    this.initNetworkMonitoring();
    this.initServerPolling();
  }

  private initNetworkMonitoring(): void {
    // Utilisation des événements natifs du navigateur (très performant)
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    this.subscription.add(
      merge(online$, offline$).subscribe(isOnline => {
        this.online.next(isOnline);
        if (isOnline) {
          this.handleOnlineState();
        } else {
          this.handleOfflineState();
        }
      })
    );

    // Vérification de la vitesse via l'API Network Information (si disponible)
    this.checkConnectionSpeed();
  }

  private checkConnectionSpeed(): void {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (connection) {
      const checkSpeed = () => {
        // downlink donne la vitesse en Mbps. Si < 1 Mbps, on considère que c'est lent.
        if (connection.downlink < 1) {
          this.showSnackbar.next(true);
          this.message.next('Connexion lente');
          this.snackbarClass.next('slow');
          setTimeout(() => this.showSnackbar.next(false), 5000);
        }
      };

      connection.addEventListener('change', checkSpeed);
      checkSpeed(); // Vérification initiale
    }
  }

  private initServerPolling(): void {
    this.subscription.add(
      this.maintenanceService.isInMaintenanceMode$.subscribe(isInMaintenance => {
        const type = this.maintenanceService.maintenanceType.value;
        if (isInMaintenance && type === 'maintenance') {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      })
    );
  }

  private startPolling(): void {
    if (this.pollingSubscription) return;

    // Polling toutes les 60 secondes
    this.pollingSubscription = interval(60000)
      .pipe(
        switchMap(() => {
          // On envoie une requête POST simple pour vérifier GraphQL
          return this.http.post(environment.httpUri, { query: '{ __typename }' }, { observe: 'response' }).pipe(
            catchError(() => of(null))
          );
        })
      )
      .subscribe(response => {
        if (response && response.status === 200) {
          this.maintenanceService.hideMaintenancePage();
          this.stopPolling();
        }
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private handleOnlineState(): void {
    if (this.previousConnectionState.value === 'offline') {
      this.showSnackbar.next(true);
      this.message.next('Connexion rétablie');
      this.snackbarClass.next('online');
      
      // On ne masque la page que si c'était une panne de connexion (offline)
      // Si c'est une maintenance serveur, c'est le polling qui s'en charge !
      if (this.maintenanceService.maintenanceType.value === 'offline') {
        this.maintenanceService?.hideMaintenancePage();
      }
      
      setTimeout(() => this.showSnackbar.next(false), 5000);
    }
    this.previousConnectionState.next('online');
  }

  private handleOfflineState(): void {
    if (this.previousConnectionState.value !== 'offline') {
      this.showSnackbar.next(true);
      this.message.next('Aucune connexion Internet');
      this.snackbarClass.next('offline');
      this.maintenanceService?.showMaintenancePage();
    }
    this.previousConnectionState.next('offline');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
